/**
 * QuantityStepper — pill stepper with round orange −/+ buttons.
 *
 * Ported 1:1 from `tw-components.jsx:347`. Deviations are mechanical only: `cx` and
 * `Icon` are imported, and there's no `import React`.
 *
 * Already fully controlled — `value` in, `onChange(clampedValue)` out — so it wires
 * straight onto app-owned quantity state. Two sizes: `md` (31px buttons) and `lg`
 * (36px). A third, smaller density exists as the CSS class `pc-compact`, which
 * `ProductCard` applies for 4-up listing grids; those three rules live in
 * ./keyframes because they're descendant selectors, not utilities.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

export function QuantityStepper({
  value = 1,
  onChange,
  min = 1,
  max = 999,
  step = 1,
  size = "md",
  style = {},
  className = "",
}) {
  const set = (v) => {
    const c = Math.max(min, Math.min(max, v));
    if (onChange) onChange(c);
  };
  const lg = size === "lg";
  const btnCls = cx(
    "dnd-stepper__btn border-0 rounded-full bg-orange-50 text-orange-700 cursor-pointer inline-flex items-center justify-center",
    lg ? "w-[36px] h-[36px]" : "w-[31px] h-[31px]",
    "[transition:background_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)]",
    "enabled:hover:bg-orange-100 enabled:active:scale-[0.85] disabled:text-neutral-300 disabled:bg-neutral-50 disabled:cursor-not-allowed"
  );
  return (
    <div
      className={cx(
        "dnd-stepper",
        lg && "dnd-stepper--lg",
        "inline-flex items-center bg-card border-[1.5px] border-line rounded-[var(--radius-pill)] p-[3px]",
        "[transition:border-color_var(--duration-fast)] focus-within:border-blue-500 focus-within:[box-shadow:var(--focus-ring)]",
        className
      )}
      style={style}
    >
      <button
        type="button"
        className={btnCls}
        aria-label="Decrease quantity"
        disabled={value <= min}
        onClick={() => set(value - step)}
      >
        <Icon name="remove" size={20} weight={600} />
      </button>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        aria-label="Quantity"
        onChange={(e) => set(parseInt(e.target.value || min, 10))}
        className={cx(
          "dnd-stepper__val text-center border-0 bg-transparent outline-none font-sans font-semibold text-ink [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          lg ? "min-w-[47px] text-md" : "min-w-[40px] text-base"
        )}
      />
      <button
        type="button"
        className={btnCls}
        aria-label="Increase quantity"
        disabled={value >= max}
        onClick={() => set(value + step)}
      >
        <Icon name="add" size={20} weight={600} />
      </button>
    </div>
  );
}
